import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import {
  FormGroup,
  FormArray,
  FormBuilder,
  Validators,
  FormControl,
  AbstractControl,
  ValidatorFn,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { AccountManagementService } from 'src/app/services/account-management.service';
import { UserManagementService } from 'src/app/services/user-management.service';
import { OrganisationService } from 'src/app/services/organisation.service';
import { StateService } from 'src/app/services/state.service';
import { BranchManagementService } from 'src/app/services/branch-management.service';
import { StorageService } from 'src/app/services/storage.service';
import { tap } from 'rxjs';
import { USER_FORM_CONSTANTS } from 'src/app/models/user-form.constants';

// Custom validator function for minimum 10 digits contact number
export function minLengthDigitsValidator(minLength: number): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    if (!control.value) {
      return null; // Return null if the control value is empty
    }

    const value = control.value.toString();
    if (value.length < minLength) {
      return {
        minLengthDigits: {
          requiredLength: minLength,
          actualLength: value.length,
        },
      };
    }

    // Return null if validation passes
    return null;
  };
}

// Custom validator to check for spaces
export function noSpacesValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    if (control.value && typeof control.value === 'string') {
      if (control.value.trim().length === 0) {
        return { noSpaces: true };
      }
    }
    return null;
  };
}

@Component({
    selector: 'app-user-form',
    templateUrl: './user-form.component.html',
    styleUrls: ['./user-form.component.scss'],
    standalone: false
})
export class UserFormComponent implements OnInit {
  readonly constants = USER_FORM_CONSTANTS;

  userId!: number;
  editMode: boolean = false;
  editUserForm: FormGroup;
  addUserForm: FormGroup;
  addnewUserDetails!: FormArray;
  submitted: boolean = false;
  userData: any = [];
  accountDetails: any = [];
  states: any = [];
  organisation: any = {};
  branchType: any = [];
  selectedBranches: any[] = [];
  selectedRoles: any;
  previousSelection: any[] = [];
  previousRoleSelection: any[] = [];
  branches: any[] = [];
  roles: any[] = [];
  // branchDetails: any = [];
  roleType: any = [];
  public phonePattern: string = this.constants.PHONE_PATTERN;
  previousEmail: string;
  previousContact: string;
  branchData: any = [];
  defaultAccountId: any;
  accountUserData: any = [];
  selectedAccount: any;
  userRole: any;
  showRole: boolean = false;
  accountId: any = [];
  loggedInUserId: string;
  userBranches: any;
  rolesData: any = [
    this.constants.ROLES.ORGANISATION_ADMIN,
    this.constants.ROLES.ACCOUNT_ADMIN,
    this.constants.ROLES.BRANCH_ADMIN,
    this.constants.ROLES.CLIENT,
  ];

  constructor(
    private activatedRouter: ActivatedRoute,
    private storageService: StorageService,
    private router: Router,
    private formBuilder: FormBuilder,
    private messageService: MessageService,
    private accountManagementService: AccountManagementService,
    private state: StateService,
    private organisationService: OrganisationService,
    private userManagementService: UserManagementService,
    private branchManagementService: BranchManagementService,
    private cdr: ChangeDetectorRef
  ) {
    this.getRoles();
    this.getBranchList();

    this.activatedRouter.params.subscribe((params) => {
      this.userId = params['id'];

      if (this.userId) {
        this.editMode = true;
        this.getOrganisation();
        this.editUserDetails(this.userId);
      }
    });

    this.editUserForm = this.formBuilder.group({
      firstName: [
        '',
        [
          Validators.required,
          noSpacesValidator(),
          this.notOnlyNumbersValidator(),
          this.noLeadingSpacesValidator(),
          this.noXssValidator,
        ],
      ],
      lastName: [
        '',
        [
          Validators.required,
          noSpacesValidator(),
          this.notOnlyNumbersValidator(),
          this.noLeadingSpacesValidator(),
          this.noXssValidator,
        ],
      ],
      email: ['', [Validators.required]],
      contact: [
        '',
        [
          Validators.required,
          Validators.maxLength(this.constants.VALIDATION.MAX_CONTACT_LENGTH),
          Validators.minLength(this.constants.VALIDATION.MIN_CONTACT_LENGTH),
        ],
      ],
      role: ['', [Validators.required]],
      status: ['', [Validators.required]],
      // branch: ['', [Validators.required]],
      // account: [''],
      accountBranchArray: this.formBuilder.array([]), // Single FormArray for both account and branch
      is_api_user: [false, Validators.required]
    });

    this.addUserForm = this.formBuilder.group({
      addnewUserDetails: this.formBuilder.array([]),
    });
    this.defaultAccountId = this.storageService.getAccountId() || null;
    this.userRole = this.storageService.getRole() || null;
    this.loggedInUserId = this.storageService.getUserID() || null;
    this.getUserDetails(this.loggedInUserId);

    if (
      this.userRole === this.constants.ROLES.ORGANISATION_ADMIN ||
      this.userRole === this.constants.ROLES.ACCOUNT_ADMIN
    ) {
      this.showRole = true;
      this.getAccountList();
    } else {
      this.showRole = false;
      this.getUserAccountData();
    }
  }
  noOnlySpacesValidator(control: FormControl) {
    if (control.value && control.value.trim().length === 0) {
      return { noOnlySpaces: true };
    }
    return null; // Validation passes
  }

  noLeadingSpacesValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const value = control.value;

      // Check if the value starts with spaces
      if (value && /^\s/.test(value)) {
        return { noLeadingSpaces: true };
      }

      return null;
    };
  }

  noXssValidator(control: FormControl) {
    if (!control.value) {
      return null;
    }

    const value = control.value.toString().toLowerCase().trim();

    const xssPatterns = [
      /<script/i, // script tags
      /<\/script>/i, // closing script tags
      /javascript:/i, // javascript: protocol
      /onerror=/i, // onerror event
      /onload=/i, // onload event
      /onclick=/i, // onclick event
      /<iframe/i, // iframe tags
      /<object/i, // object tags
      /<embed/i, // embed tags
      /<img[^>]*on/i, // img with events
      /eval\(/i, // eval function
      /%3cscript/i, // encoded <script
      /&lt;script/i, // HTML entity encoded
    ];

    const isMalicious = xssPatterns.some((pattern) => pattern.test(value));

    return isMalicious ? { xssDetected: true } : null;
  }

  notOnlyNumbersValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const value = control.value;

      // Check if the value contains only numbers or numbers with spaces afterward
      if (value && (/^\d+$/.test(value) || /^\d+\s+$/.test(value))) {
        return { notOnlyNumbers: true };
      }

      return null;
    };
  }

  handlePasteEvent(event: ClipboardEvent) {
    const paste = event.clipboardData.getData('text');
    // Regular expression to match only alphanumeric characters and spaces
    const regex = new RegExp(this.constants.ALPHANUMERIC_SPACE_PATTERN);
    if (!regex.test(paste)) {
      event.preventDefault();
    }
  }

  ngOnInit() {
    this.addnewUserDetail();

    this.getStates();
    this.getOrganisation();
  }

  getUserDetails(id) {
    this.userManagementService.getUsersDetailsById(id).subscribe((res: any) => {
      this.userData = res['content'][0];
      this.userBranches = this.userData.branches;
    });
  }

  getStates() {
    this.state.getState().subscribe((res: any) => {
      this.states = res['content'];
    });
  }

  getOrganisation() {
    this.organisationService.getOrganisationDetails().subscribe((res: any) => {
      this.organisation = res['content'];
    });
  }

  getAccountList() {
    this.accountManagementService
      .getAccountDetails('')
      .subscribe((res: any) => {
        this.accountDetails = res['content'] || [];
        this.accountUserData = [...(res['content'] || [])];

        this.getAccountDetailsById();
        if (!this.editMode && this.accountUserData.length > 0) {
          this.autoSelectFirstAccountForAddFormAdmin();
        }
        this.cdr.detectChanges();
      });
  }

  private autoSelectFirstAccountForAddFormAdmin() {
    const firstAccount = this.accountUserData[0];
    if (firstAccount && this.userDetailsArray().length > 0) {
      const firstUserGroup = this.userDetailsArray().at(0) as FormGroup;
      firstUserGroup.patchValue({ account: firstAccount });
      const accountId = firstAccount.id || firstAccount.account_id;
      if (accountId) {
        this.branchManagementService.getBranchPerUser(accountId).subscribe((res) => {
          this.branchData[0] = res['content'];
          if (this.userRole === this.constants.ROLES.BRANCH_ADMIN) {
            this.branchData[0] = this.branchData[0].filter((branch) =>
              this.userBranches.includes(branch.id)
            );
          }
          if (this.branchData[0]?.length > 0) {
            firstUserGroup.patchValue({ branch: [this.branchData[0][0].id] });
          }
        });
      }
    }
  }

  getBranchList() {
    this.branchManagementService.getAllBranches(0).subscribe((res: any) => {
      this.branchType = res['content'];
      this.branchType = this.branchType.filter(
        (element) => element.is_active === true
      );
    });
  }

  getRoles() {
    this.userManagementService.userRoleFilter().subscribe((res: any) => {
      let roles = res['content']['roles'];
      this.roleType = roles.filter((role: any) => role !== this.constants.ROLES.FINANCE_ADMIN);
    });
  }

  removeUser(index: number) {
    this.userDetailsArray().removeAt(index);
    this.addUserForm.markAsUntouched();
    this.addUserForm.markAsPristine();
    this.submitted = false;
    this.userDetailsArray().updateValueAndValidity();
  }

  userDetailsArray(): FormArray {
    return this.addUserForm.get(
      this.constants.FORM_FIELDS.ADD_NEW_USER_DETAILS
    ) as FormArray;
  }

  addnewUserDetail() {
    if (
      this.addUserForm.controls[this.constants.FORM_FIELDS.ADD_NEW_USER_DETAILS]
        .valid
    ) {
      this.userDetailsArray().push(this.inituserDetails());
      // Get the newly added form group
      const newIndex = this.userDetailsArray().length - 1;
      const newUserFormGroup = this.userDetailsArray().at(
        newIndex
      ) as FormGroup;

      // Reset validation state
      this.resetFormValidationState(newUserFormGroup);
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: this.constants.MESSAGES.WARNING.NEW_USER,
        detail: this.constants.MESSAGES.WARNING.FILL_DETAILS,
      });
    }
  }
  private resetFormValidationState(formGroup: FormGroup) {
    formGroup.markAsUntouched();
    formGroup.markAsPristine();

    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      if (control) {
        control.markAsUntouched();
        control.markAsPristine();
      }
    });
  }
  inituserDetails() {
    const formGroup = this.formBuilder.group({
      firstName: new FormControl('', [
        Validators.required,
        noSpacesValidator(),
        this.notOnlyNumbersValidator(),
        this.noLeadingSpacesValidator(),
        this.noXssValidator,
      ]),
      lastName: new FormControl('', [
        Validators.required,
        noSpacesValidator(),
        this.notOnlyNumbersValidator(),
        this.noLeadingSpacesValidator(),
        this.noXssValidator,
      ]),
      email: new FormControl('', [Validators.required]),
      contact: [
        '',
        [
          Validators.required,
          Validators.maxLength(this.constants.VALIDATION.MAX_CONTACT_LENGTH),
          Validators.minLength(this.constants.VALIDATION.MIN_CONTACT_LENGTH),
        ],
      ],
      role: new FormControl('', [Validators.required]),
      branch: new FormControl('', [Validators.required]),
      status: new FormControl(this.constants.STATUS.ACTIVE, [
        Validators.required,
      ]),
      password: new FormControl('', [
        Validators.required,
        Validators.minLength(this.constants.VALIDATION.MIN_PASSWORD_LENGTH),
        Validators.pattern(this.constants.PASSWORD_PATTERN),
      ]),
      cpassword: new FormControl('', [Validators.required]),
      account: [''],
      is_api_user: [false, Validators.required]
    });
    formGroup.markAsPristine();
    formGroup.markAsUntouched();
    return formGroup;
  }
  //  Validators.pattern('^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[^A-Za-z\\d]).{8,}$')
  //  Validators.pattern('(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[$@$!%*?&])[A-Za-z\d$@$!%*?&].{8,}')

  numberOnly(event: any) {
    const pattern = /[0-9\-]/;
    let inputChar = String.fromCharCode(event.charCode);
    if (
      event.keyCode != this.constants.KEY_CODES.BACKSPACE &&
      !pattern.test(inputChar)
    ) {
      event.preventDefault();
    }
  }

  gotoDashboard() {
    this.userRole === 'CLIENT'
      ? this.router.navigate([this.constants.ROUTES.DASHBOARD])
      : this.router.navigate(['doqfy/admin-dashboard']);
  }

  gotoListing() {
    this.router.navigate([this.constants.ROUTES.USER_LISTING]);
  }

  handleBranchSelection(event: any) {
    // const currentSelection = event.value; // Get the current selected branch IDs

    const accountBranchArray = this.editUserForm.get(
      this.constants.FORM_FIELDS.ACCOUNT_BRANCH_ARRAY
    ).value;
    const selectedArrayBranch = [];
    accountBranchArray.forEach((element) => {
      if (Array.isArray(element.branch)) {
        element.branch.forEach((branch) => {
          selectedArrayBranch.push(branch);
        });
      }
    });
    const currentSelection = [...new Set(selectedArrayBranch)];

    // Update the selected branches and previous selection
    this.selectedBranches = currentSelection;
    this.previousSelection = currentSelection.slice(); // Make a copy of the current selection
  }

  handleRoleSelection(event: any) {
    const currentSelection = event.value; // Get the current selected branch IDs

    this.selectedRoles = currentSelection;
    this.previousRoleSelection = currentSelection.slice(); // Make a copy of the current selection
    // const accountBranchArray = this.editUserForm.get('accountBranchArray') as FormArray;
    // this.branchData.forEach((element, index) => {
    //   const branch = [];
    //   this.branchData[index].forEach(element => {
    //     if (this.selectedRoles === 'ORGANISATION-ADMIN' || this.selectedRoles === 'ACCOUNT-ADMIN') {
    //     //  element.disabled = true;
    //       branch.push(element.id)
    //     } else {
    //     //  element.disabled = false;
    //     }
    //   });
    //   accountBranchArray.at(index).setValue({ branch: branch })

    // });
  }

  editUserDetails(id: any) {
    let branchData = [];
    this.userManagementService.getUsersDetailsById(id).subscribe((res: any) => {
      this.userData = res['content'][0];

      if (this.userData.id === Number(id)) {
        this.branches = this.userData.branches.map((branch) => branch);
        this.previousSelection = this.branches;
        this.previousEmail = this.userData.email;
        this.previousContact = this.userData.contact_number;

        this.editUserForm.patchValue({
          firstName: this.userData.first_name,
          lastName: this.userData.last_name,
          email: this.userData.email,
          contact: this.userData.contact_number,
          role: this.userData.roles[0],
          status: this.userData.is_active
            ? this.constants.STATUS.ACTIVE
            : this.constants.STATUS.INACTIVE,
          // account: this.userData.accounts,
          // branch: this.userData.branches
        });
        this.accountId = this.userData.accounts;
        const accountBranchArray = this.editUserForm.get(
          this.constants.FORM_FIELDS.ACCOUNT_BRANCH_ARRAY
        ) as FormArray;

        this.userData.accounts.forEach((account, index) => {
          this.addAccountBranch();
          this.branchManagementService
            .getBranchPerUser(account)
            .subscribe((res) => {
              this.branchData[index] = res['content'] || [];
              const availableIds = new Set(this.branchData[index].map((b: any) => b.id));
              const filteredBranches = this.branches.filter((id: any) => availableIds.has(id));
              accountBranchArray
                .at(index)
                .setValue({ account: account, branch: filteredBranches });
              this.cdr.detectChanges();
            });
        });
        this.cdr.detectChanges();
      }
    });
  }
  //---------------------------------------------------------Account & branch ---------------------------------------//

  initAccountBranch(): FormGroup {
    return this.formBuilder.group({
      account: ['', Validators.required],
      branch: ['', Validators.required],
    });
  }

  // Method to add a new account-branch pair
  addAccountBranch() {
    const accountBranchArray = this.editUserForm.get(
      this.constants.FORM_FIELDS.ACCOUNT_BRANCH_ARRAY
    ) as FormArray;
    const newGroup = this.initAccountBranch();
    accountBranchArray.push(newGroup);
    const index = accountBranchArray.length - 1;

    // Auto-select the first account and its first branch if accounts are available
    if (this.accountUserData && this.accountUserData.length > 0) {
      const firstAccount = this.accountUserData[0];
      const accountVal = this.showRole
        ? (firstAccount.id !== undefined ? firstAccount.id : firstAccount.account_id)
        : (firstAccount.account_id !== undefined ? firstAccount.account_id : firstAccount.id);

      newGroup.patchValue({ account: accountVal });

      const fetchAccountId = firstAccount.account_id || firstAccount.id;
      if (fetchAccountId) {
        this.branchManagementService.getBranchPerUser(fetchAccountId).subscribe((res) => {
          this.branchData[index] = res['content'] || [];
          if (this.userRole === this.constants.ROLES.BRANCH_ADMIN && this.userBranches) {
            this.branchData[index] = this.branchData[index].filter((branch: any) =>
              this.userBranches.includes(branch.id)
            );
          }
          if (this.branchData[index]?.length > 0) {
            newGroup.patchValue({ branch: [this.branchData[index][0].id] });
          }
          this.cdr.detectChanges();
        });
      }
    }
    this.cdr.detectChanges();
  }

  // Method to remove an account-branch pair by index
  removeAccountBranch(index: number) {
    const accountBranchArray = this.editUserForm.get(
      this.constants.FORM_FIELDS.ACCOUNT_BRANCH_ARRAY
    ) as FormArray;

    if (accountBranchArray.length > 1) {
      // Optional: Ensure there's always at least one entry
      accountBranchArray.removeAt(index);
      this.branchData.splice(index, 1);
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: this.constants.MESSAGES.WARNING.CANNOT_REMOVE,
        detail: this.constants.MESSAGES.WARNING.MIN_ONE_ACCOUNT_BRANCH,
      });
    }
  }

  get accountBranchArray(): FormArray {
    return this.editUserForm.get(
      this.constants.FORM_FIELDS.ACCOUNT_BRANCH_ARRAY
    ) as FormArray;
  }

  //-------------------------------------------------------------------------------------------------------------------------//

  saveuser() {
    this.submitted = true;

    const addnewuserDetailsFormArray = this.addUserForm.get(
      this.constants.FORM_FIELDS.ADD_NEW_USER_DETAILS
    ) as FormArray;

    let hasXss = false;
    addnewuserDetailsFormArray.controls.forEach(
      (abstractControl: AbstractControl) => {
        if (abstractControl instanceof FormGroup) {
          const formGroup = abstractControl as FormGroup;
          if (
            formGroup.get('firstName')?.hasError('xssDetected') ||
            formGroup.get('lastName')?.hasError('xssDetected')
          ) {
            hasXss = true;
          }
        }
      }
    );

    if (hasXss) {
      this.messageService.add({
        severity: 'error',
        summary: 'Security Alert',
        detail:
          'Malicious content detected! Script tags and harmful code are not allowed.',
      });
      return;
    }

    if (this.addUserForm.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please fill the required field correctly',
      });
      return;
    }

    const userDetailsArray: any[] = [];

    // Iterate over each FormGroup in the FormArray
    addnewuserDetailsFormArray.controls.forEach(
      (abstractControl: AbstractControl) => {
        if (abstractControl instanceof FormGroup) {
          const formGroup = abstractControl as FormGroup;
          var userDetail = {
            first_name:
              formGroup.controls[this.constants.FORM_FIELDS.FIRST_NAME].value,
            last_name:
              formGroup.controls[this.constants.FORM_FIELDS.LAST_NAME].value,
            email: formGroup.controls[this.constants.FORM_FIELDS.EMAIL].value,
            contact_number:
              formGroup.controls[this.constants.FORM_FIELDS.CONTACT].value,
            roles: [formGroup.controls[this.constants.FORM_FIELDS.ROLE].value],
            branch_id:
              formGroup.controls[this.constants.FORM_FIELDS.BRANCH].value,
            password:
              formGroup.controls[this.constants.FORM_FIELDS.PASSWORD].value,
            confirm_password:
              formGroup.controls[this.constants.FORM_FIELDS.CPASSWORD].value,
            accounts:
              formGroup.controls[this.constants.FORM_FIELDS.ACCOUNT].value.id,
            is_an_api_user: formGroup.get('is_api_user')?.value
          };
          userDetailsArray.push(userDetail);
        }
      }
    );

    this.userManagementService
      .createUser(userDetailsArray)
      .subscribe((res: any) => {
        if (res?.status === 201 && res?.message) {
          const emailMatch = res.message.match(/'([^']+)'/);
          const successEmail = emailMatch ? emailMatch[1] : null;
          this.messageService.add({
            severity: 'success',
            summary: this.constants.MESSAGES.SUCCESS.USER_CREATED,
            detail: res.message,
          });
          setTimeout(() => {
             this.router.navigate([this.constants.ROUTES.USER_LISTING]);
           }, this.constants.TIMING.REDIRECT_DELAY);
           
          if (successEmail) {
            for (let i = addnewuserDetailsFormArray.length - 1; i >= 0; i--) {
              const formGroup = addnewuserDetailsFormArray.at(i) as FormGroup;
              const email = formGroup.get(
                this.constants.FORM_FIELDS.EMAIL
              )?.value;

              if (email === successEmail) {
                addnewuserDetailsFormArray.removeAt(i);
                break;
              }
            }
          }
          if (addnewuserDetailsFormArray.length === 0) {
            setTimeout(() => {
              this.router.navigate([this.constants.ROUTES.USER_LISTING]);
            }, this.constants.TIMING.REDIRECT_DELAY);
          } else {
            this.submitted = false;
          }
        } else if (res?.content) {
          const errors = res.content.errors || [];
          const success = res.content.success || [];
          if (errors.length > 0) {
            errors.forEach((errMsg: string) => {
              this.messageService.add({
                severity: 'error',
                summary: this.constants.MESSAGES.ERROR.USER_CREATION_FAILED,
                detail: errMsg,
              });
            });
          }
          const successfulEmails = success
            .map((msg: string) => {
              const match = msg.match(/'([^']+)'/);
              return match ? match[1] : null;
            })
            .filter((email) => email !== null);
          for (let i = addnewuserDetailsFormArray.length - 1; i >= 0; i--) {
            const formGroup = addnewuserDetailsFormArray.at(i) as FormGroup;
            const email = formGroup.get(
              this.constants.FORM_FIELDS.EMAIL
            )?.value;

            if (successfulEmails.includes(email)) {
              addnewuserDetailsFormArray.removeAt(i);
            }
          }
          if (success.length > 0) {
            this.messageService.add({
              severity: 'success',
              summary: this.constants.MESSAGES.SUCCESS.USERS_CREATED,
              detail: `${success.length} user${
                success.length > 1 ? 's' : ''
              } created successfully.`,
            });
          }
          if (errors.length === 0 && success.length > 0) {
            setTimeout(() => {
              this.router.navigate([this.constants.ROUTES.USER_LISTING]);
            }, this.constants.TIMING.REDIRECT_DELAY);
          }
          if (addnewuserDetailsFormArray.length > 0) {
            this.submitted = false;
          }
        } else {
          this.messageService.add({
            severity: 'error',
            summary: this.constants.MESSAGES.ERROR.ERROR,
            detail: this.constants.MESSAGES.ERROR.UNEXPECTED_RESPONSE,
          });
          this.submitted = false;
        }
      });
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach((field) => {
      const control = formGroup.get(field);
      control?.markAsTouched({ onlySelf: true });
      control?.updateValueAndValidity();
    });
  }

  edituser() {
    this.submitted = true;

    if (
      this.editUserForm.get('firstName')?.hasError('xssDetected') ||
      this.editUserForm.get('lastName')?.hasError('xssDetected')
    ) {
      this.messageService.add({
        severity: 'error',
        summary: 'Security Alert',
        detail:
          'Malicious content detected! Script tags and harmful code are not allowed.',
      });
      return;
    }

    if (this.editUserForm.invalid) {
      this.markFormGroupTouched(this.editUserForm);
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please fill the required field correctly',
      });
      return;
    }

    if (this.editUserForm.valid) {
      const accountBranchArray = this.editUserForm.get(
        this.constants.FORM_FIELDS.ACCOUNT_BRANCH_ARRAY
      ).value;
      for (let i = 0; i < accountBranchArray.length; i++) {
        for (let j = i + 1; j < accountBranchArray.length; j++) {
          const itemA = accountBranchArray[i];
          const itemB = accountBranchArray[j];

          if (itemA.account && itemB.account && itemA.account === itemB.account) {
            const branchesA = Array.isArray(itemA.branch) ? itemA.branch : [itemA.branch];
            const branchesB = Array.isArray(itemB.branch) ? itemB.branch : [itemB.branch];
            const hasCommonBranch = branchesA.some((b: any) => branchesB.includes(b));

            if (hasCommonBranch || (branchesA.length === 0 && branchesB.length === 0)) {
              this.messageService.add({
                severity: 'error',
                summary: 'Duplicate Entry',
                detail: 'Duplicate account and branch selection found. Please select different accounts or branches.',
              });
              return;
            } else {
              this.messageService.add({
                severity: 'error',
                summary: 'Duplicate Account',
                detail: 'Duplicate accounts found. Please choose a different account or combine branches under one account.',
              });
              return;
            }
          }
        }
      }

      const selectedArrayBranch = [];
      const selectedArrayAccount = [];

      accountBranchArray.forEach((element, index) => {
        if (Array.isArray(element.branch)) {
          element.branch.forEach((branch) => {
            if (this.branchData[index]) {
              this.branchData[index].forEach((bElement) => {
                if (bElement.id === branch) {
                  selectedArrayBranch.push(branch);
                }
              });
            }
          });
        }
        selectedArrayAccount.push(element.account);
      });
      const uniqueArrayBranch = [...new Set(selectedArrayBranch)];
      const uniqueArrayAccount = [...new Set(selectedArrayAccount)];

      var userFormData = [
        {
          first_name:
            this.editUserForm.controls[this.constants.FORM_FIELDS.FIRST_NAME]
              .value,
          last_name:
            this.editUserForm.controls[this.constants.FORM_FIELDS.LAST_NAME]
              .value,
          email:
            this.editUserForm.controls[this.constants.FORM_FIELDS.EMAIL].value,
          contact_number:
            this.editUserForm.controls[this.constants.FORM_FIELDS.CONTACT]
              .value,
          roles: [
            this.editUserForm.controls[this.constants.FORM_FIELDS.ROLE].value,
          ],
          user_id: this.userId,
          add_branch: uniqueArrayBranch,
          remove_branch: this.branches.filter(
            (branch) => !uniqueArrayBranch.includes(branch)
          ),
          branches: uniqueArrayBranch,
          accounts: uniqueArrayAccount,
        },
      ];
      // ---------------------------Deleting the email and contact number if same as privious ---------------------------------------------//
      if (this.previousEmail === userFormData[0].email) {
        delete userFormData[0].email;
      }
      if (this.previousContact === userFormData[0].contact_number) {
        delete userFormData[0].contact_number;
      }

      //--------------------------------------------------------------------------------------------------------------//

      this.userManagementService
        .editUser(userFormData)
        .subscribe((res: any) => {
          if (res.status == 200) {
            this.messageService.add({
              severity: 'success',
              summary: this.constants.MESSAGES.SUCCESS.USER_UPDATED,
              detail: this.constants.MESSAGES.SUCCESS.USER_EDITED,
            });
            setTimeout(() => {
              this.router.navigate([this.constants.ROUTES.USER_LISTING]);
            }, this.constants.TIMING.REDIRECT_DELAY);
          }
        });
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: this.constants.MESSAGES.WARNING.INVALID_DETAILS,
        detail: this.constants.MESSAGES.WARNING.ENTER_VALID_DETAILS,
      });
    }
  }

  getuserDetails() {
    this.userManagementService.getUsersDetails().subscribe((res: any) => {
      this.userData = res['content'];
    });
  }

  onPasswordChange(i: number) {
    const password = this.userDetailsArray()
      .at(i)
      .get(this.constants.FORM_FIELDS.PASSWORD).value;
    const cpassword = this.userDetailsArray()
      .at(i)
      .get(this.constants.FORM_FIELDS.CPASSWORD).value;
    if (password === cpassword) {
      this.userDetailsArray()
        .at(i)
        .get(this.constants.FORM_FIELDS.CPASSWORD)
        .setErrors(null);
    } else {
      this.userDetailsArray()
        .at(i)
        .get(this.constants.FORM_FIELDS.CPASSWORD)
        .setErrors({ mismatch: true });
    }
  }

  numberOnlyForContact(event: any) {
    const pattern = /[0-9\-]/;
    let inputChar = String.fromCharCode(event.charCode);
    if (
      (event.keyCode != this.constants.KEY_CODES.BACKSPACE &&
        !pattern.test(inputChar)) ||
      event.keyCode == this.constants.KEY_CODES.HYPHEN
    ) {
      event.preventDefault();
    } else if (event.target.value === '0') {
      event.preventDefault();
    }
  }

  getUserAccountData() {
    this.accountManagementService.getAccountPerUser().subscribe((res) => {
      this.accountUserData = [...(res['content'] || [])];
      this.getAccountDetailsById();
      if (!this.editMode && this.accountUserData.length > 0) {
        this.autoSelectFirstAccountForAddForm();
      }
      this.cdr.detectChanges();
    });
  }

  private autoSelectFirstAccountForAddForm() {
    const firstAccount = this.accountUserData[0];
    if (firstAccount && this.userDetailsArray().length > 0) {
      const firstUserGroup = this.userDetailsArray().at(0) as FormGroup;
      firstUserGroup.patchValue({ account: firstAccount });
      // Load branches for the first account and auto-select the first branch
      const accountId = firstAccount.account_id || firstAccount.id;
      if (accountId) {
        this.branchManagementService.getBranchPerUser(accountId).subscribe((res) => {
          this.branchData[0] = res['content'];
          if (this.userRole === this.constants.ROLES.BRANCH_ADMIN) {
            this.branchData[0] = this.branchData[0].filter((branch) =>
              this.userBranches.includes(branch.id)
            );
          }
          if (this.branchData[0]?.length > 0) {
            firstUserGroup.patchValue({ branch: [this.branchData[0][0].id] });
          }
        });
      }
    }
  }

  onSelectedAccount(event, index) {
    const account = event.value;
    this.accountId.push(event.value);

    // Reset branch control for this index in edit mode
    const accountBranchArray = this.editUserForm.get(
      this.constants.FORM_FIELDS.ACCOUNT_BRANCH_ARRAY
    ) as FormArray;
    if (accountBranchArray && accountBranchArray.at(index)) {
      accountBranchArray.at(index).patchValue({ branch: [] });
    }

    this.getSelectedAccount(event.value, index, true);
  }

  onSelectedAccountData(event, i) {
    const account = event.value;
    this.accountId = [];
    if (this.showRole) {
      this.accountId = account.id;
    } else {
      this.accountId = account.account_id;
    }

    // Reset branch control for this index in add mode
    if (this.userDetailsArray() && this.userDetailsArray().at(i)) {
      this.userDetailsArray().at(i).patchValue({ branch: [] });
    }

    this.getSelectedAccount(this.accountId, i, true);
  }

  getAccountDetailsById() {
    this.accountManagementService
      .getAccountDetail(this.defaultAccountId)
      .subscribe((res) => {
        const userAccount = res['content'][0];
        const exists = this.accountUserData.some(
          (element) => element.account_id === userAccount.id
        );

        if (!exists) {
          userAccount.account_id = userAccount.id;
          // this.accountUserData.push(userAccount);
        }
        this.accountUserData = Array.from(new Set(this.accountUserData));
        this.selectedAccount = userAccount.account_id;

        if (!this.editMode && this.userDetailsArray().length > 0) {
          const firstUserGroup = this.userDetailsArray().at(0) as FormGroup;
          const matchingAccount = this.accountUserData.find(
            (a) => (a.account_id || a.id) === this.selectedAccount
          );
          if (matchingAccount && !firstUserGroup.get('account')?.value) {
            firstUserGroup.patchValue({ account: matchingAccount });
          }
        }

        if (this.accountId.length === 0) {
          this.getSelectedAccount(this.defaultAccountId, 0);
        }
      });
  }

  getSelectedAccount(id, index, isUserChange: boolean = false) {
    if (id) {
      this.branchManagementService.getBranchPerUser(id).subscribe((res) => {
        this.branchData[index] = res['content'] || [];
        if (this.userRole === this.constants.ROLES.BRANCH_ADMIN) {
          const filteredBranchData = this.branchData[index].filter((branch) =>
            this.userBranches.includes(branch.id)
          );
          this.branchData[index] = filteredBranchData;
        }

        if (this.branchData[index]?.length > 0) {
          const firstBranchId = this.branchData[index][0].id;

          if (this.editMode) {
            const accountBranchArray = this.editUserForm.get(
              this.constants.FORM_FIELDS.ACCOUNT_BRANCH_ARRAY
            ) as FormArray;
            const group = accountBranchArray?.at(index) as FormGroup;
            if (group && (isUserChange || !group.get('branch')?.value?.length)) {
              group.patchValue({ branch: [firstBranchId] });
              this.handleBranchSelection(null);
            }
          } else {
            const userGroup = this.userDetailsArray().at(index) as FormGroup;
            if (userGroup && (isUserChange || !userGroup.get('branch')?.value?.length)) {
              userGroup.patchValue({ branch: [firstBranchId] });
            }
          }
        } else {
          // If no branches returned, clear the selection
          if (this.editMode) {
            const accountBranchArray = this.editUserForm.get(
              this.constants.FORM_FIELDS.ACCOUNT_BRANCH_ARRAY
            ) as FormArray;
            accountBranchArray?.at(index)?.patchValue({ branch: [] });
            this.handleBranchSelection(null);
          } else {
            this.userDetailsArray()?.at(index)?.patchValue({ branch: [] });
          }
        }
        this.cdr.detectChanges();
      });
    }
  }
}
